/**
 * Create Market Page Component
 * Full page view for creating new prediction markets
 */

import React, { useState, useEffect } from 'react';
import { useAdmin } from '../store/AdminContext';
import { apiService } from '../services/apiService';
import { allCountries, Country } from '../data/countries';
import './CreateMarketPage.css';

type AnswerType = 'yesno' | 'multiple' | 'number';
type EndTimeType = 'duration' | 'date';
type DurationUnit = 'Hours' | 'Days' | 'Weeks' | 'Months';

const CreateMarketPage: React.FC = () => {
  const { setCurrentPage } = useAdmin();
  
  // Basic Information
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');

  // Timing
  const [endTimeType, setEndTimeType] = useState<EndTimeType>('duration');
  const [durationValue, setDurationValue] = useState<number>(7);
  const [durationUnit, setDurationUnit] = useState<DurationUnit>('Days');
  const [endDate, setEndDate] = useState('');

  // Answer Type
  const [answerType, setAnswerType] = useState<AnswerType>('yesno');
  const [multipleChoiceOptions, setMultipleChoiceOptions] = useState<string[]>(['Option 1', 'Option 2']);

  // Stake Limits
  const [minStake, setMinStake] = useState<number>(5);
  const [maxStake, setMaxStake] = useState<number>(1000);

  // Image URL
  const [imageUrl, setImageUrl] = useState<string>('');

  // Geo Restrictions
  const [geoRestriction, setGeoRestriction] = useState<'all' | 'specific'>('all');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [allowedCountries, setAllowedCountries] = useState<Country[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load allowed countries from global settings on mount
  useEffect(() => {
    const loadAllowedCountries = async () => {
      try {
        // Try to get allowed regions/countries from settings
        const regions = await apiService.getAllowedRegions();
        if (regions.allowedRegions && regions.allowedRegions.length > 0) {
          // Filter countries based on allowed regions
          const filtered = allCountries.filter(c => 
            regions.allowedRegions.includes(c.code)
          );
          setAllowedCountries(filtered.length > 0 ? filtered : allCountries);
        } else {
          // If no restrictions, show all countries
          setAllowedCountries(allCountries);
        }
      } catch (err) {
        // If API fails, use all countries
        console.error('Failed to load allowed countries:', err);
        setAllowedCountries(allCountries);
      }
    };
    loadAllowedCountries();
  }, []);

  const handleSubmit = async (e: React.FormEvent, isDraft: boolean = false) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validation
    if (!headline || !description || !category) {
      setError('Headline, Description, and Category are required');
      return;
    }

    if (endTimeType === 'duration' && (!durationValue || durationValue < 1)) {
      setError('Duration value must be at least 1');
      return;
    }

    if (endTimeType === 'date' && !endDate) {
      setError('End date is required');
      return;
    }

    if (endTimeType === 'date' && endDate) {
      const selectedDate = new Date(endDate);
      const now = new Date();
      if (selectedDate <= now) {
        setError('End date must be in the future');
        return;
      }
    }

    if (answerType === 'multiple' && multipleChoiceOptions.length < 2) {
      setError('Multiple choice requires at least 2 options');
      return;
    }

    if (!minStake || !maxStake || minStake < 1 || maxStake < minStake) {
      setError('Valid stake limits are required');
      return;
    }

    setIsLoading(true);
    try {
      // Prepare market data
      const marketData: any = {
        headline,
        description,
        category,
        answerType,
        minStake,
        maxStake,
        status: isDraft ? 'draft' : 'pending',
      };

      // Set end time
      if (endTimeType === 'duration') {
        marketData.endTimeType = 'duration';
        marketData.durationValue = durationValue;
        marketData.durationUnit = durationUnit;
        marketData.endDate = null; // Explicitly set to null for duration type
      } else {
        marketData.endTimeType = 'date';
        if (endDate) {
          // Convert datetime-local format to ISO 8601 format
          // datetime-local gives "YYYY-MM-DDTHH:mm" format, need to convert to ISO 8601
          const date = new Date(endDate);
          if (!isNaN(date.getTime())) {
            marketData.endDate = date.toISOString();
          } else {
            marketData.endDate = null;
          }
        } else {
          marketData.endDate = null;
        }
      }

      // Set answer options if multiple choice
      if (answerType === 'multiple') {
        marketData.answerOptions = multipleChoiceOptions;
      }

      // Set image URL (always send, even if empty)
      marketData.imageUrl = imageUrl || null;

      // Set geo restrictions
      if (geoRestriction === 'all') {
        marketData.geoRestriction = 'all';
        marketData.allowedCountries = null;
      } else {
        marketData.geoRestriction = 'specific';
        marketData.allowedCountries = selectedCountries.length > 0 ? selectedCountries : null;
      }

      // Create market
      await apiService.createMarket(marketData);
      
      if (isDraft) {
        setSuccess('Market saved as draft successfully!');
        resetForm();
        setTimeout(() => {
          setCurrentPage('predictions-overview');
        }, 1500);
      } else {
        setSuccess('Market published successfully!');
        resetForm();
        setTimeout(() => {
          setCurrentPage('predictions-overview');
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create market');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setHeadline('');
    setDescription('');
    setCategory('');
    setEndTimeType('duration');
    setDurationValue(7);
    setDurationUnit('Days');
    setEndDate('');
    setAnswerType('yesno');
    setMultipleChoiceOptions(['Option 1', 'Option 2']);
    setMinStake(5);
    setMaxStake(1000);
    setImageUrl('');
    setGeoRestriction('all');
    setSelectedCountries([]);
    setError(null);
    setSuccess(null);
  };

  const toggleCountry = (countryCode: string) => {
    if (selectedCountries.includes(countryCode)) {
      setSelectedCountries(selectedCountries.filter(c => c !== countryCode));
    } else {
      setSelectedCountries([...selectedCountries, countryCode]);
    }
  };

  const addOption = () => {
    setMultipleChoiceOptions([...multipleChoiceOptions, `Option ${multipleChoiceOptions.length + 1}`]);
  };

  const removeOption = (index: number) => {
    if (multipleChoiceOptions.length > 2) {
      setMultipleChoiceOptions(multipleChoiceOptions.filter((_, i) => i !== index));
    }
  };

  const updateOption = (index: number, value: string) => {
    const updated = [...multipleChoiceOptions];
    updated[index] = value;
    setMultipleChoiceOptions(updated);
  };

  const renderPreviewCard = () => {
    const previewTitle = headline || 'Your prediction title here...';
    const previewCat = category || 'Category';
    const previewImage = imageUrl || 'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?w=400';
    const previewOptions = answerType === 'yesno' ? ['Yes', 'No'] : 
                          answerType === 'multiple' ? multipleChoiceOptions.slice(0, 4) : 
                          answerType === 'number' ? [] : [];

    return (
      <div className="prediction-card" style={{ maxWidth: '100%', cursor: 'default' }}>
        <div 
          className="prediction-card-image"
          style={{
            height: '180px',
            backgroundImage: `linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%), url(${previewImage})`
          }}
        >
          <div className="prediction-card-overlay">
            <div className="prediction-timer">7d<span>|</span>00h<span>|</span>00m</div>
            <button className="prediction-share-btn" type="button">📤</button>
          </div>
          <div className="prediction-creator">
            <div className="prediction-creator-avatar">Y</div>
            <div className="prediction-creator-info">
              <div className="prediction-creator-label">Created by</div>
              <div className="prediction-creator-name">You</div>
            </div>
          </div>
        </div>
        <div className="prediction-card-content">
          <div className="prediction-engagement">
            <div className="engagement-item">❤️ 0</div>
            <div className="engagement-item">💬 0</div>
            <button className="share-earn-btn" type="button">Share & Earn</button>
          </div>
          <div className="prediction-title">{previewTitle}</div>
          <div style={{ marginBottom: '12px' }}>
            <span className="status blue">{previewCat}</span>
          </div>
          {previewOptions.length > 0 && (
            <div className="prediction-options">
              {previewOptions.map((opt, idx) => (
                <div key={idx} className="prediction-option">{opt}</div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="create-market-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Create Market</h1>
          <p className="page-sub">Create a new prediction market</p>
        </div>
      </div>

      <div className="approval-warning">
        <span className="approval-warning-icon">🔒</span>
        <div className="approval-warning-content">
          <h3>Dual Approval Required</h3>
          <p>This market will need approval from another admin before going live</p>
        </div>
      </div>

      <div className="grid-2">
        <div>
          <div className="content-card">
            <form onSubmit={(e) => handleSubmit(e, false)} className="create-market-form">
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {success && (
                <div className="success-message">
                  {success}
                </div>
              )}

              {/* Basic Information */}
              <div className="form-section">
                <div className="form-section-title">📝 Basic Information</div>
                <div className="form-group">
                  <label className="form-label">Title / Question *</label>
                  <input
                    type="text"
                    value={headline}
                    onChange={(e) => setHeadline(e.target.value)}
                    placeholder="e.g. Will Bitcoin reach $100k by end of 2025?"
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    placeholder="Detailed description..."
                    disabled={isLoading}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      disabled={isLoading}
                      required
                    >
                      <option value="">Select...</option>
                      <option value="Sports">🏆 Sports</option>
                      <option value="Crypto">💹 Crypto</option>
                      <option value="Tech">📱 Tech</option>
                      <option value="Politics">🏛️ Politics</option>
                      <option value="Entertainment">🎬 Entertainment</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Image URL</label>
                    <input
                      type="text"
                      value={imageUrl}
                      onChange={(e) => setImageUrl(e.target.value)}
                      placeholder="https://..."
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {/* Answer Type */}
              <div className="form-section">
          <div className="form-section-title">⏰ Timing</div>
          <div className="form-group">
            <label className="form-label">End Time *</label>
            <div className="form-tabs">
              <button
                type="button"
                className={`form-tab ${endTimeType === 'duration' ? 'active' : ''}`}
                onClick={() => setEndTimeType('duration')}
                disabled={isLoading}
              >
                Duration
              </button>
              <button
                type="button"
                className={`form-tab ${endTimeType === 'date' ? 'active' : ''}`}
                onClick={() => setEndTimeType('date')}
                disabled={isLoading}
              >
                Specific Date
              </button>
            </div>
            {endTimeType === 'duration' ? (
              <div className="duration-inputs">
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '11px' }}>Value</label>
                  <input
                    type="number"
                    value={durationValue}
                    onChange={(e) => setDurationValue(parseInt(e.target.value) || 1)}
                    placeholder="e.g. 7"
                    min={1}
                    disabled={isLoading}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label" style={{ fontSize: '11px' }}>Unit</label>
                  <select
                    value={durationUnit}
                    onChange={(e) => setDurationUnit(e.target.value as DurationUnit)}
                    disabled={isLoading}
                    required
                  >
                    <option>Hours</option>
                    <option>Days</option>
                    <option>Weeks</option>
                    <option>Months</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="form-group">
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                  disabled={isLoading}
                  required
                />
              </div>
            )}
          </div>
        </div>

        {/* Answer Type */}
        <div className="form-section">
          <div className="form-section-title">🎯 Answer Type *</div>
          <div className="answer-types">
            <div
              className={`answer-type ${answerType === 'yesno' ? 'selected' : ''}`}
              onClick={() => !isLoading && setAnswerType('yesno')}
            >
              <div className="answer-type-icon">✅❌</div>
              <div className="answer-type-name">Binary</div>
              <div className="answer-type-desc">Yes / No</div>
            </div>
            <div
              className={`answer-type ${answerType === 'multiple' ? 'selected' : ''}`}
              onClick={() => !isLoading && setAnswerType('multiple')}
            >
              <div className="answer-type-icon">📋</div>
              <div className="answer-type-name">Multiple</div>
              <div className="answer-type-desc">Choose options</div>
            </div>
            <div
              className={`answer-type ${answerType === 'number' ? 'selected' : ''}`}
              onClick={() => !isLoading && setAnswerType('number')}
            >
              <div className="answer-type-icon">🔢</div>
              <div className="answer-type-name">Numeric</div>
              <div className="answer-type-desc">Number/Price</div>
            </div>
          </div>
          {answerType === 'multiple' && (
            <div className="multiple-choice-options">
              <label className="form-label">Answer Options</label>
              <div className="options-list">
                {multipleChoiceOptions.map((option, index) => (
                  <div key={index} className="option-row">
                    <span className="option-num">{index + 1}</span>
                    <input
                      type="text"
                      value={option}
                      onChange={(e) => updateOption(index, e.target.value)}
                      placeholder={`Option ${index + 1}`}
                      disabled={isLoading}
                      required
                    />
                    {multipleChoiceOptions.length > 2 && (
                      <button
                        type="button"
                        className="action-button red"
                        onClick={() => removeOption(index)}
                        disabled={isLoading}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="button-secondary"
                onClick={addOption}
                disabled={isLoading}
                style={{ marginTop: '10px' }}
              >
                + Add Option
              </button>
            </div>
          )}
        </div>

        {/* Stake Limits */}
        <div className="form-section">
          <div className="form-section-title">💰 Stake Limits</div>
          <div className="stake-inputs">
            <div className="form-group">
              <label className="form-label">Minimum Stake *</label>
              <div className="input-prefix">
                <span>£</span>
                <input
                  type="number"
                  value={minStake}
                  onChange={(e) => setMinStake(parseFloat(e.target.value) || 1)}
                  placeholder="5"
                  min={1}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Maximum Stake *</label>
              <div className="input-prefix">
                <span>£</span>
                <input
                  type="number"
                  value={maxStake}
                  onChange={(e) => setMaxStake(parseFloat(e.target.value) || 1)}
                  placeholder="1000"
                  min={1}
                  disabled={isLoading}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        {/* Geo Restrictions */}
        <div className="form-section">
          <div className="form-section-title">🌍 Geo Restrictions</div>
          <div className="form-check" style={{ marginBottom: '12px' }}>
            <input
              type="radio"
              name="geo"
              id="geo-all"
              checked={geoRestriction === 'all'}
              onChange={() => {
                setGeoRestriction('all');
                setSelectedCountries([]);
              }}
              disabled={isLoading}
            />
            <label htmlFor="geo-all" style={{ marginLeft: '8px', cursor: 'pointer' }}>
              🌍 Show to All Users (Global)
            </label>
          </div>
          <div className="form-check">
            <input
              type="radio"
              name="geo"
              id="geo-specific"
              checked={geoRestriction === 'specific'}
              onChange={() => setGeoRestriction('specific')}
              disabled={isLoading}
            />
            <label htmlFor="geo-specific" style={{ marginLeft: '8px', cursor: 'pointer' }}>
              🎯 Specific Countries Only
            </label>
          </div>
          {geoRestriction === 'specific' && (
            <div style={{ marginTop: '16px' }}>
              <label className="form-label">Select Countries</label>
              <div className="country-tags">
                {allowedCountries.map((country) => (
                  <div
                    key={country.code}
                    className={`country-tag ${selectedCountries.includes(country.code) ? 'selected' : ''}`}
                    onClick={() => !isLoading && toggleCountry(country.code)}
                    style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
                  >
                    {country.flag} {country.name}
                  </div>
                ))}
              </div>
              {selectedCountries.length > 0 && (
                <div style={{ marginTop: '12px', fontSize: '13px', color: 'var(--text-muted)' }}>
                  {selectedCountries.length} {selectedCountries.length === 1 ? 'country' : 'countries'} selected
                </div>
              )}
            </div>
          )}
        </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button
                  type="button"
                  className="button-secondary"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={isLoading}
                >
                  💾 Save Draft
                </button>
                <button
                  type="submit"
                  className="button-primary"
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : '📤 Submit for Approval'}
                </button>
              </div>
            </form>
          </div>
        </div>

        <div style={{ position: 'sticky', top: '24px', alignSelf: 'flex-start' }}>
          <div className="content-card">
            <div className="card-head">
              <span className="card-title">📱 Live Preview</span>
            </div>
            <div className="card-body" style={{ background: '#0f1219', borderRadius: '0 0 var(--radius-lg) var(--radius-lg)' }}>
              {renderPreviewCard()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMarketPage;
